import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Clock, Send, Calendar, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TravelContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! Brandi will get back to you within 24-48 hours.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or email directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-gold font-medium tracking-widest text-sm uppercase">Get In Touch</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 mb-6">
            Contact Us
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Have a question about travel planning? Want to learn more about our services? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl text-navy mb-8">Let's Connect</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Email</h3>
                    <a 
                      href="mailto:brandi@livelifeglobalwellness.com"
                      className="text-text-secondary hover:text-gold transition-colors"
                    >
                      brandi@livelifeglobalwellness.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Location</h3>
                    <p className="text-text-secondary">Houston, TX</p>
                    <p className="text-text-muted text-sm">Serving clients nationwide</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy mb-1">Response Time</h3>
                    <p className="text-text-secondary">Within 24-48 hours</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-12 space-y-4">
                <h3 className="font-semibold text-navy mb-4">Quick Actions</h3>
                
                <Link to="/consultation" className="block">
                  <div className="bg-cream rounded-xl p-4 hover:shadow-card-hover transition-all flex items-center gap-4 group">
                    <div className="w-10 h-10 bg-gold rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-navy" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-navy group-hover:text-gold transition-colors">Book a Consultation</p>
                      <p className="text-sm text-text-muted">$25 - Credited toward your booking</p>
                    </div>
                  </div>
                </Link>

                <a href="/#inquiry" className="block">
                  <div className="bg-cream rounded-xl p-4 hover:shadow-card-hover transition-all flex items-center gap-4 group">
                    <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-navy group-hover:text-gold transition-colors">Submit Travel Inquiry</p>
                      <p className="text-sm text-text-muted">Tell us about your dream vacation</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="bg-cream rounded-3xl p-8 shadow-card">
                <h2 className="font-serif text-2xl text-navy mb-6">
                  Send a Message
                </h2>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-navy font-medium">Your Name *</Label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Email Address *</Label>
                    <Input 
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Subject *</Label>
                    <select
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2 focus:border-gold focus:ring-gold"
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Question</option>
                      <option value="destination">Destination Information</option>
                      <option value="cruise">Cruise Inquiry</option>
                      <option value="resort">Resort Inquiry</option>
                      <option value="group">Group Travel</option>
                      <option value="wedding">Destination Wedding</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Message *</Label>
                    <Textarea 
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="How can we help you?"
                      rows={5}
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold py-4 rounded-full text-lg"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TravelContactPage;
